import gql from 'graphql-tag';
import React, {ReactNode} from 'react';

import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {GetPipelineConfigSpecType} from 'pipelines/StepShapeUtils';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {ButtonSize} from 'ui-library/buttons/Button';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {MenuWidth, MenuHeight} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import {JSONValue} from 'util/Types';

import CyclingSelector from '../CyclingSelector';
import {getDataFrameInfoOrEmptyInfo, useStepConfigEditorContext} from '../StepConfigEditorContext';
import {
  GetDataFrameUniqueValues,
  GetDataFrameUniqueValuesVariables,
} from './__generated/GetDataFrameUniqueValues';

const MAX_UNIQUE_VALUES = 1000;

type ColumnValueSelectorProps = Omit<DataFrameColumnValueEditorProps, 'configShape'> & {
  dataFrame: GetDataFrameUniqueValues['dataFrame'] | null;
};

function ColumnValueSelector({
  dataFrame,
  config,
  onChangeConfig,
  noValueSelectedText,
  buttonSize,
  disabled,
  dataTestAttrs,
  renderCyclingSelector = false,
}: ColumnValueSelectorProps): JSX.Element {
  const uniqueValues = dataFrame != null ? assertIsAccessible(dataFrame).uniqueValues : [];
  const optionDatas = uniqueValues.map((value) => ({name: value, value}));
  let displayName: ReactNode;
  if (config == null) {
    displayName = noValueSelectedText ?? 'None';
  } else if (typeof config === 'object') {
    displayName = JSON.stringify(config);
  } else {
    displayName = config;
  }

  const selector = (
    <SelectDropdown
      displayName={displayName}
      menu={
        <SearchableMenu
          height={MenuHeight.MEDIUM}
          selectedValue={config}
          onSelect={onChangeConfig}
          width={MenuWidth.FULL}
          optionDatas={optionDatas}
        />
      }
      buttonSize={buttonSize}
      disabled={disabled}
      dataTestAttrs={dataTestAttrs}
    />
  );

  return renderCyclingSelector ? (
    <CyclingSelector
      selector={selector}
      disabled={disabled}
      options={uniqueValues}
      selectedOption={config}
      onChange={onChangeConfig}
    />
  ) : (
    selector
  );
}

export const GET_DATA_FRAME_UNIQUE_VALUES_QUERY = gql`
  query GetDataFrameUniqueValues(
    $dataFrameId: StrictString!
    $columnSystemName: StrictString!
    $limit: StrictInt!
  ) @skipAudit @engComponent(name: DATA_FRAMES) {
    dataFrame(id: $dataFrameId) {
      ...InaccessibleInfoGraphQL
      ... on DataFrameNode {
        id
        uniqueValues(columnSystemName: $columnSystemName, limit: $limit)
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface DataFrameColumnValueEditorProps {
  buttonSize?: ButtonSize;
  config: JSONValue;
  configShape: GetPipelineConfigSpecType<'columnValue'>;
  dataTestAttrs?: DataTestAttrs;
  disabled?: boolean;
  noValueSelectedText?: string;
  onChangeConfig: (config: JSONValue) => void;
  renderCyclingSelector?: boolean;
}

export default function DataFrameColumnValueEditor({
  buttonSize,
  config,
  configShape,
  dataTestAttrs,
  disabled,
  noValueSelectedText,
  onChangeConfig,
  renderCyclingSelector = false,
}: DataFrameColumnValueEditorProps): JSX.Element {
  const {dataFramesInfos} = useStepConfigEditorContext();
  const {id: dataFrameId} = getDataFrameInfoOrEmptyInfo(
    dataFramesInfos,
    configShape.inputDataName,
    configShape.inputDataIndex
  );

  const commonProps = {
    config,
    onChangeConfig,
    noValueSelectedText,
    buttonSize,
    disabled,
    dataTestAttrs,
    renderCyclingSelector,
  };

  // If no dataFrameId is available, return an empty dropdown
  if (dataFrameId == null) {
    return <ColumnValueSelector {...commonProps} dataFrame={null} />;
  }

  return (
    <BenchlingQuery<GetDataFrameUniqueValues, GetDataFrameUniqueValuesVariables>
      query={GET_DATA_FRAME_UNIQUE_VALUES_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{
        dataFrameId,
        columnSystemName: configShape.columnSystemName,
        limit: MAX_UNIQUE_VALUES,
      }}
    >
      {({dataFrame}) => <ColumnValueSelector {...commonProps} dataFrame={dataFrame} />}
    </BenchlingQuery>
  );
}
